import React, { useContext, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MyContext } from "../../../context/MyContext";
import socratesApi from "../../../api/socratesApi";
import Btn from "../../../components/btn";
import AirHeader from "../../../components/AirHeader";
import SocratesLoading from "../../../components/SocratesLoading";
import { AlertModal } from "../../../components/overlay";
import { Chevron, Required } from "../../../assets/svgs/icons";

interface InterviewCategory {
  id?: number;
  name: string;
}

interface InterestingOption {
  value: number;
  label: string;
}

/**
 * SocratesCreateContainer
 * "나만의 소크라테스를 만들어보세요" 페이지
 * 기존 InterviewForm.js를 TypeScript로 마이그레이션
 * 피그마 디자인 기반 UI
 */
const SocratesCreateContainer: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useContext(MyContext);
  const fileRef = useRef<HTMLInputElement>(null);

  // API에서 가져온 옵션 목록
  const [interviewCategory, setInterviewCategory] = useState<
    InterviewCategory[]
  >([]);
  const [interestingList, setInterestingList] = useState<InterestingOption[]>(
    []
  );

  // 폼 데이터
  const [interviewInfo, setInterviewInfo] = useState({
    interview_category: "",
    interviewing_institution: "",
    favorite_topic: "",
    favorite_subject: "",
    special_request: "",
    user_input: "",
  });

  const [fileName, setFileName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);

  // 관심영역 드롭다운 상태
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 컴포넌트 마운트 시 옵션 목록 로드
  useEffect(() => {
    loadCategoryAndInterests();
  }, []);

  // 드롭다운 외부 클릭 시 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const loadCategoryAndInterests = async () => {
    try {
      // 면접 유형 로드
      const categoryData = await (
        socratesApi as any
      ).getInterviewCategories?.();
      if (categoryData) {
        setInterviewCategory(
          Array.isArray(categoryData)
            ? categoryData
            : categoryData.kind_list || []
        );
      }

      // 관심 영역 로드
      const interestData = await (socratesApi as any).getInterestingAreas?.();
      if (interestData) {
        const listData = Array.isArray(interestData)
          ? interestData
          : interestData.inter_area_list || [];

        const mapped = listData.map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setInterestingList(mapped);
      }
    } catch (error) {
      console.error("옵션 로드 실패:", error);
      setErrorMessage("설정 로드에 실패했습니다. 다시 시도해주세요.");
    }
  };

  // 면접 유형 선택
  const selectInterviewCategory = (value: string) => {
    setInterviewInfo((prev) => ({
      ...prev,
      interview_category: value,
    }));
  };

  // 관심 영역 선택
  const selectFavTopic = (label: string) => {
    setInterviewInfo((prev) => ({
      ...prev,
      favorite_topic: label,
    }));
  };

  // 허용된 파일 확장자 목록
  const ALLOWED_EXTENSIONS = [
    ".pdf",
    ".xlsx",
    ".txt",
    ".hwp",
    ".hwpx",
    ".doc",
    ".docx",
  ];

  // 파일 확장자 검증
  const isValidFileExtension = (filename: string): boolean => {
    const ext = filename.toLowerCase().substring(filename.lastIndexOf("."));
    return ALLOWED_EXTENSIONS.includes(ext);
  };

  // 파일 제거 처리
  const handleFileClear = () => {
    if (fileRef.current) {
      fileRef.current.value = "";
    }
    setFileName("");
    setInterviewInfo((prev) => ({
      ...prev,
      special_request: "",
    }));
  };

  // 파일 업로드 처리
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 파일 확장자 검증
    if (!isValidFileExtension(file.name)) {
      setErrorMessage(
        "지원하지 않는 파일 형식입니다. hwp(x), doc(x), pdf, xlsx, txt 파일만 업로드 가능합니다."
      );
      setShowErrorModal(true);
      if (fileRef.current) {
        fileRef.current.value = "";
      }
      return;
    }

    setFileName(file.name);
    try {
      const formData = new FormData();
      formData.append("file", file);

      // 파일 업로드 API 호출
      const result = await (socratesApi as any).uploadFile?.(formData);
      if (result) {
        setInterviewInfo((prev) => ({
          ...prev,
          special_request: result.result || result,
        }));
      }
    } catch (err) {
      console.error("파일 업로드 실패:", err);
      setErrorMessage(
        "파일 업로드에 실패했습니다. 지원하는 형식을 확인해주세요."
      );
      setShowErrorModal(true);
      setFileName("");
      if (fileRef.current) {
        fileRef.current.value = "";
      }
      setInterviewInfo((prev) => ({
        ...prev,
        special_request: "",
      }));
    }
  };

  // 폼 검증 및 제출
  const checkInputValues = async () => {
    let text = "";
    if (interviewInfo?.interview_category === "") {
      text = "면접 유형을 선택해주세요.";
    } else if (interviewInfo?.interviewing_institution === "") {
      text = "지원하는 기관을 입력해주세요.";
    } else if (interviewInfo?.favorite_topic === "") {
      text = "관심있는 영역 및 진로학과를 선택해주세요.";
    } else if (interviewInfo?.favorite_subject === "") {
      text = "관심있는 주제를 입력해주세요.";
    } else if (interviewInfo?.user_input === "") {
      text = "원하는 스타일을 입력해주세요.";
    }

    if (text !== "") {
      setErrorMessage(text);
      setShowErrorModal(true);
      return;
    }

    // 키워드 배열로 변환
    const keywordsArray = interviewInfo.favorite_subject
      .split(",")
      .map((k) => k.trim())
      .filter((k) => k);

    // FormData 객체 생성
    const formDataObj = new FormData();
    formDataObj.append("userId", user?.id?.toString() || "");
    formDataObj.append("interviewType", interviewInfo.interview_category);
    formDataObj.append("interviewSubject", interviewInfo.favorite_topic);
    formDataObj.append(
      "targetInstitution",
      interviewInfo.interviewing_institution
    );

    // 키워드 배열 추가
    keywordsArray.forEach((keyword) => {
      formDataObj.append("keywords", keyword);
    });

    // 파일 내용 추가 (선택사항)
    if (interviewInfo.special_request) {
      formDataObj.append("personalStatement", interviewInfo.special_request);
    }

    // 추가 요구사항 (선택사항)
    if (interviewInfo.user_input) {
      formDataObj.append("additionalRequirements", interviewInfo.user_input);
    }

    // 면접 생성
    try {
      setLoading(true);
      const result = await (socratesApi as any).createInterview?.(formDataObj);
      if (result && result.interviewId) {
        // 생성된 면접으로 네비게이트
        navigate(`/socrates/${result.interviewId}`);
      } else {
        setErrorMessage("면접 생성에 실패했습니다. 다시 시도해주세요.");
      }
    } catch (error) {
      console.error("면접 생성 실패:", error);
      setErrorMessage("면접 생성에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setLoading(false);
    }
  };

  // 로딩 중일 때 로딩 화면 표시
  if (loading) {
    return <SocratesLoading message="면접을 준비하는 중입니다..." />;
  }

  return (
    <div className="flex flex-col w-full h-full min-h-0 overflow-hidden">
      {/* Header with Logo */}
      <AirHeader type="main" />

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 py-8 min-h-0">
        <div className="max-w-2xl mx-auto space-y-8">
          {/* Page Title - Figma: style_0JKE8K */}
          <div className="space-y-2">
            <h1 className="text-2xl font-bold text-[#212121] leading-[1.5em] tracking-[-0.02em]">
              나만의 소크라테스를 만들어보세요.
            </h1>
          </div>

          {/* 삭제됨: 인라인 에러 메시지는 모달로 대체 */}

          {/* 1. Interview Category Selection */}
          <div className="flex flex-col gap-3">
            {/* Label - Figma: style_DLLUBA */}
            <div className="flex flex-col gap-1">
              <div className="flex gap-[2px] items-start">
                <h3 className="text-base font-bold text-[#212121] leading-[1.5em] tracking-[-0.02em]">
                  면접유형
                </h3>
                <Required
                  className="w-2 h-2 fill-[#6D2EF2]"
                  style={{ aspectRatio: "1/1" }}
                />
              </div>
              {/* Helper Text - Figma: style_TZ2IKV */}
              <p className="text-sm font-normal text-[#888888] leading-[1.43em] tracking-[-0.02em]">
                원하는 면접 유형을 선택하세요.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              {interviewCategory?.map((item, idx) => (
                <button
                  key={`interview-category-${idx}`}
                  onClick={() => selectInterviewCategory(item.name)}
                  className={`px-5 py-2 h-9 rounded-[18px] border transition text-sm font-semibold leading-[1.43em] tracking-[-0.02em] ${
                    interviewInfo?.interview_category === item.name
                      ? "border-[#6D2EF2] bg-[#6D2EF2] text-white"
                      : "border-[#C5ABFA] bg-white text-[#C5ABFA] hover:border-[#6D2EF2]"
                  }`}
                >
                  {item.name}
                </button>
              ))}
            </div>
          </div>

          {/* 2. Institution Input */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex gap-[2px] items-start">
                <h3 className="text-base font-bold text-[#212121] leading-[1.5em] tracking-[-0.02em]">
                  기관명
                </h3>
                <Required
                  className="w-2 h-2 fill-[#6D2EF2]"
                  style={{ aspectRatio: "1/1" }}
                />
              </div>
              <p className="text-sm font-normal text-[#888888] leading-[1.43em] tracking-[-0.02em]">
                지원하는 기관명을 입력하세요.
              </p>
            </div>
            <input
              name="interviewing_institution"
              placeholder="ex) 서울시교육청 영재교육원"
              value={interviewInfo.interviewing_institution}
              onChange={(e) =>
                setInterviewInfo((prev) => ({
                  ...prev,
                  interviewing_institution: e.target.value,
                }))
              }
              className="w-full px-3 py-3 border border-[#EEEEEE] rounded-lg text-base font-medium text-[#212121] leading-[1.5em] tracking-[-0.02em] placeholder:text-[12px] placeholder:text-[#888888] focus:outline-none focus:ring-1 focus:ring-[#6D2EF2] focus:border-[#6D2EF2]"
            />
          </div>

          {/* 3. Favorite Topic Selection - 커스텀 드롭다운 */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex gap-[2px] items-start">
                <h3 className="text-base font-bold text-[#212121] leading-[1.5em] tracking-[-0.02em]">
                  관심영역
                </h3>
                <Required
                  className="w-2 h-2 fill-[#6D2EF2]"
                  style={{ aspectRatio: "1/1" }}
                />
              </div>
              <p className="text-sm font-normal text-[#888888] leading-[1.43em] tracking-[-0.02em]">
                관심있는 영역 및 진로학과를 선택하세요.
              </p>
            </div>
            {/* 피그마 디자인 기반 커스텀 드롭다운 */}
            <div className="relative" ref={dropdownRef}>
              {/* 드롭다운 트리거 */}
              <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full h-[48px] px-3 bg-white border border-[#EEEEEE] rounded-lg flex items-center justify-between cursor-pointer"
              >
                <span
                  className={`text-[12px] tracking-[-0.02em] leading-[16px] ${
                    interviewInfo.favorite_topic
                      ? "text-[#212121] font-medium text-[16px] leading-[24px]"
                      : "text-[#888888] font-normal text-[12px] "
                  }`}
                >
                  {interviewInfo.favorite_topic || "선택해 주세요."}
                </span>
                <Chevron
                  className={`w-6 h-6 text-[#212121] transition-transform ${
                    isDropdownOpen ? "-rotate-90" : "rotate-90"
                  }`}
                />
              </button>

              {/* 드롭다운 리스트 */}
              {isDropdownOpen && (
                <div
                  className="absolute top-[52px] left-0 w-full bg-white rounded-lg p-2 z-50"
                  style={{
                    boxShadow:
                      "0px 6px 12px -6px rgba(0,0,0,0.12), 0px 8px 24px -4px rgba(0,0,0,0.08)",
                  }}
                >
                  {interestingList?.map((item) => (
                    <button
                      key={item.value}
                      type="button"
                      onClick={() => {
                        selectFavTopic(item.label);
                        setIsDropdownOpen(false);
                      }}
                      className={`w-full h-[40px] px-3 py-2 rounded-lg text-left text-[14px] font-normal text-[#888888] tracking-[-0.02em] leading-[20px] transition-colors ${
                        interviewInfo.favorite_topic === item.label
                          ? "bg-[#F7F7F7]"
                          : "hover:bg-[#F7F7F7]"
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* 4. Keywords Input */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex gap-[2px] items-start">
                <h3 className="text-base font-bold text-[#212121] leading-[1.5em] tracking-[-0.02em]">
                  관심주제
                </h3>
                <Required
                  className="w-2 h-2 fill-[#6D2EF2]"
                  style={{ aspectRatio: "1/1" }}
                />
              </div>
              <p className="text-sm font-normal text-[#888888] leading-[1.43em] tracking-[-0.02em]">
                관심있는 주제의 키워드를 입력하세요.
              </p>
            </div>
            <textarea
              name="favorite_subject"
              maxLength={200}
              placeholder="쉼표(,)로 구분해 주세요. 여러 가지 키워드를 입력할 수 있어요."
              value={interviewInfo.favorite_subject}
              onChange={(e) =>
                setInterviewInfo((prev) => ({
                  ...prev,
                  favorite_subject: e.target.value,
                }))
              }
              rows={3}
              className="w-full px-3 py-3 border border-[#EEEEEE] rounded-lg text-base font-medium text-[#212121] leading-[1.5em] tracking-[-0.02em] placeholder:text-[#888888] placeholder:text-[12px] focus:outline-none focus:ring-1 focus:ring-[#6D2EF2] focus:border-[#6D2EF2] resize-none"
            />
            <p className="text-xs text-[#888888]">
              {interviewInfo.favorite_subject.length}/200
            </p>
          </div>

          {/* 5. File Upload */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <h3 className="text-base font-bold text-[#212121] leading-[1.5em] tracking-[-0.02em]">
                파일 업로드
              </h3>
              <p className="text-sm font-normal text-[#888888] leading-[1.43em] tracking-[-0.02em]">
                자기소개 또는 탐구 보고서가 있다면 파일을 업로드해주세요.
              </p>
            </div>
            <div className="flex gap-1 items-center">
              <div className="flex-1 relative h-[48px]">
                <input
                  readOnly
                  value={
                    fileName ||
                    "업로드 가능한 파일 형식: hwp(x), doc(x), pdf, xlsx, txt"
                  }
                  className={`w-full h-full px-3 pr-10 border border-[#EEEEEE] rounded-lg font-normal leading-[1.5em] tracking-[-0.02em] bg-white truncate ${
                    fileName
                      ? "text-[14px] text-[#212121]"
                      : "text-[12px] text-[#888888]"
                  }`}
                />
                {fileName && (
                  <button
                    type="button"
                    onClick={handleFileClear}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-[#888888] hover:text-[#666666] transition"
                    aria-label="파일 제거"
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 5L5 15M5 5L15 15"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                )}
              </div>
              <label
                htmlFor="file_upload_v2_socrates"
                className="px-5 h-[48px] bg-[#212121] text-white font-semibold text-sm leading-[1.43em] tracking-[-0.02em] rounded-lg cursor-pointer hover:bg-[#333333] transition flex items-center justify-center whitespace-nowrap flex-shrink-0"
              >
                파일 업로드
              </label>
            </div>
            <input
              type="file"
              id="file_upload_v2_socrates"
              ref={fileRef}
              accept=".pdf,.xlsx,.txt,.hwp,.hwpx,.doc,.docx"
              onChange={handleFileUpload}
              style={{ display: "none" }}
            />
          </div>

          {/* 6. Additional Style Input */}
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <div className="flex gap-[2px] items-start">
                <h3 className="text-base font-bold text-[#212121] leading-[1.5em] tracking-[-0.02em]">
                  질문형식
                </h3>
                <Required
                  className="w-2 h-2 fill-[#6D2EF2]"
                  style={{ aspectRatio: "1/1" }}
                />
              </div>
              <p className="text-sm font-normal text-[#888888] leading-[1.43em] tracking-[-0.02em]">
                좀 더 구체적으로 원하는 질문 형식이 있다면 적어주세요.
              </p>
            </div>
            <textarea
              name="user_input"
              placeholder="ex) 관심있는 주제 위주로 질문해 줘. 초등학생 수준에 맞게 질문해 줘."
              value={interviewInfo.user_input}
              onChange={(e) =>
                setInterviewInfo((prev) => ({
                  ...prev,
                  user_input: e.target.value,
                }))
              }
              rows={3}
              className="w-full px-3 py-3 border border-[#EEEEEE] rounded-lg text-base font-medium text-[#212121] leading-[1.5em] tracking-[-0.02em] placeholder:text-[#888888] placeholder:text-[12px] focus:outline-none focus:ring-1 focus:ring-[#6D2EF2] focus:border-[#6D2EF2] resize-none"
            />
          </div>
        </div>
      </div>

      {/* Footer - Sticky Button (Full Width matching content area) */}
      <div className="flex-shrink-0 border-t border-[#EEEEEE] p-4 bg-white">
        <div className="max-w-2xl mx-auto">
          <Btn.Primary
            onClick={checkInputValues}
            disabled={
              loading ||
              !interviewInfo.interview_category ||
              !interviewInfo.interviewing_institution ||
              !interviewInfo.favorite_topic ||
              !interviewInfo.favorite_subject ||
              !interviewInfo.user_input
            }
            className="!w-full md:!w-full"
          >
            {loading ? "생성 중..." : "모의 면접 시작"}
          </Btn.Primary>
        </div>
      </div>

      {/* 검증 오류 모달 */}
      <AlertModal
        open={showErrorModal}
        onOpenChange={setShowErrorModal}
        title="입력 확인"
        message={errorMessage}
      />
    </div>
  );
};

export default SocratesCreateContainer;
